import { Component, type ErrorInfo, type ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui';
import { ErrorState } from '@/components/shared/EmptyState';
import { PublicShell } from './PublicShell';

interface Props {
  children: ReactNode;
  fallback: (error: Error) => ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches a render error and shows something instead of a blank page.
 *
 * The fallback is supplied by the caller because the two audiences need different
 * answers. A recruiter is inside the console and can navigate out of the broken page;
 * a candidate has no account, no navigation and no administrator to tell.
 */
class RenderBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console -- the browser console is the only sink here
    console.error('Unhandled render error', error, info.componentStack);
  }

  override render(): ReactNode {
    return this.state.error ? this.props.fallback(this.state.error) : this.props.children;
  }
}

const reload = (
  <Button variant="secondary" onClick={() => window.location.reload()}>
    <RefreshCw />
    Reload
  </Button>
);

/**
 * A render error in one page should not blank the whole console. This keeps the shell
 * (and therefore the navigation out of the broken page) intact.
 */
export function AppShellBoundary({ children }: { children: ReactNode }) {
  return (
    <RenderBoundary
      fallback={(error) => (
        <ErrorState
          title="This page ran into a problem"
          description={
            import.meta.env.DEV
              ? error.message
              : 'Reloading usually clears it. If it keeps happening, let your administrator know.'
          }
          action={reload}
        />
      )}
    >
      {children}
    </RenderBoundary>
  );
}

/**
 * The same protection for the pages a candidate sees — apply, book, interview.
 *
 * These sit outside the console, so without a boundary a render error leaves a stranger
 * on a blank white page, mid-application or mid-interview, with nothing to read and no
 * way back. They would not report it and the recruiter would never learn of it; the
 * application would simply never arrive.
 *
 * The wording differs from the console's on purpose. A candidate has no administrator,
 * and the one thing they most need to know is that nothing they already submitted has
 * been thrown away.
 */
export function PublicPageBoundary({ children }: { children: ReactNode }) {
  return (
    <RenderBoundary
      fallback={(error) => (
        <PublicShell>
          <ErrorState
            title="This page ran into a problem"
            description={
              import.meta.env.DEV
                ? error.message
                : 'Nothing you have already submitted has been lost. Reloading usually clears it — if it keeps happening, reply to the email that brought you here and the hiring team will sort it out.'
            }
            action={reload}
          />
        </PublicShell>
      )}
    >
      {children}
    </RenderBoundary>
  );
}
